import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, Heart, Eye, Linkedin, Twitter, MessageCircle, Link2, Check, Mail } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [likes, setLikes] = useState(0);
  const [views, setViews] = useState(0);
  const [hasLiked, setHasLiked] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);

  // Scroll to top when component mounts or slug changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Check if user has already liked this post
  useEffect(() => {
    const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
    setHasLiked(likedPosts.includes(slug));
  }, [slug]);

  // Fetch blog post
  useEffect(() => {
    const fetchPost = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API}/blogs/${slug}`);
        if (response.data.success) {
          setPost(response.data.blog);
          setLikes(response.data.blog.likes || 0);
          setViews(response.data.blog.views || 0);
        } else {
          setError('Blog post not found');
        }
      } catch (err) {
        setError('Blog post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  // Increment view count (once per session)
  useEffect(() => {
    const incrementView = async () => {
      const viewedPosts = JSON.parse(sessionStorage.getItem('viewedPosts') || '[]');
      if (!viewedPosts.includes(slug)) {
        try {
          const response = await axios.post(`${API}/blogs/${slug}/view`);
          if (response.data.success) {
            setViews(response.data.views);
            sessionStorage.setItem('viewedPosts', JSON.stringify([...viewedPosts, slug]));
          }
        } catch (err) {
          console.error('Error incrementing view:', err);
        }
      }
    };
    if (post) {
      incrementView();
    }
  }, [slug, post]);

  const handleLike = async () => {
    if (hasLiked) return;
    
    try {
      const response = await axios.post(`${API}/blogs/${slug}/like`);
      if (response.data.success) {
        setLikes(response.data.likes);
        setHasLiked(true);
        const likedPosts = JSON.parse(localStorage.getItem('likedPosts') || '[]');
        localStorage.setItem('likedPosts', JSON.stringify([...likedPosts, slug]));
        toast.success('Thanks for the love!');
      }
    } catch (err) {
      toast.error('Failed to like post');
    }
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || 'Check out this article';
    const text = post?.excerpt || '';
    
    const shareUrls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(`${title} - ${url}`)}`
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'noopener,noreferrer');
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setLinkCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy link');
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    
    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/newsletter/subscribe`, { email: newsletterEmail });
      if (response.data.success) {
        toast.success(response.data.message);
        setNewsletterEmail('');
      }
    } catch (err) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setSubscribing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">
          <div className="blog-post-loading">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-page">
        <div className="blog-post-container">
          <div className="blog-post-error">
            <h2>Blog post not found</h2>
            <p>The article you're looking for doesn't exist or has been removed.</p>
            <button className="btn-primary" onClick={() => navigate('/')}>
              <ArrowLeft size={18} /> Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-post-page" data-testid="blog-post-page">
      <div className="blog-post-container">
        <button className="back-button" onClick={() => navigate('/')} data-testid="back-button">
          <ArrowLeft size={18} /> ← Back to Home
        </button>

        <article className="blog-post-content">
          <header className="blog-post-header">
            {post.category && (
              <span className="blog-post-category">{post.category}</span>
            )}
            <h1 className="blog-post-title">{post.title}</h1>
            
            <div className="blog-post-meta">
              {post.author && (
                <span className="meta-item">
                  <User size={16} />
                  {post.author}
                </span>
              )}
              {post.published_date && (
                <span className="meta-item">
                  <Calendar size={16} />
                  {formatDate(post.published_date)}
                </span>
              )}
              {post.read_time && (
                <span className="meta-item">
                  <Clock size={16} />
                  {post.read_time}
                </span>
              )}
              <span className="meta-item" data-testid="view-count">
                <Eye size={16} />
                {views} views
              </span>
            </div>
          </header>

          {post.featured_image && (
            <div className="blog-post-featured-image">
              <img src={post.featured_image} alt={post.title} />
            </div>
          )}

          {post.excerpt && (
            <div className="blog-post-excerpt">
              <p>{post.excerpt}</p>
            </div>
          )}

          <div 
            className="blog-post-body"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />
        </article>

        {/* Engagement Section - Like & Share */}
        <div className="blog-engagement-section" data-testid="engagement-section">
          <div className="engagement-row">
            {/* Like Button */}
            <button 
              className={`like-button ${hasLiked ? 'liked' : ''}`}
              onClick={handleLike}
              disabled={hasLiked}
              data-testid="like-button"
            >
              <Heart size={20} fill={hasLiked ? '#ef4444' : 'none'} />
              <span>{likes} {likes === 1 ? 'Like' : 'Likes'}</span>
            </button>

            {/* Share Buttons */}
            <div className="share-buttons">
              <span className="share-label">Share:</span>
              <button 
                className="share-btn linkedin" 
                onClick={() => handleShare('linkedin')}
                title="Share on LinkedIn"
                data-testid="share-linkedin"
              >
                <Linkedin size={18} />
              </button>
              <button 
                className="share-btn twitter" 
                onClick={() => handleShare('twitter')}
                title="Share on Twitter"
                data-testid="share-twitter"
              >
                <Twitter size={18} />
              </button>
              <button 
                className="share-btn whatsapp" 
                onClick={() => handleShare('whatsapp')}
                title="Share on WhatsApp"
                data-testid="share-whatsapp"
              >
                <MessageCircle size={18} />
              </button>
              <button 
                className="share-btn copy-link" 
                onClick={handleCopyLink}
                title="Copy Link"
                data-testid="share-copy"
              >
                {linkCopied ? <Check size={18} /> : <Link2 size={18} />}
              </button>
            </div>
          </div>
        </div>

        {/* Author Bio */}
        {post.author && (
          <div className="blog-author-bio" data-testid="author-bio">
            {post.author_photo && (
              <img src={post.author_photo} alt={post.author} className="author-bio-photo" />
            )}
            <div className="author-bio-info">
              <p className="author-bio-name">{post.author}</p>
              {post.author_bio && <p className="author-bio-text">{post.author_bio}</p>}
            </div>
          </div>
        )}

        {/* Newsletter Subscription */}
        <div className="newsletter-section" data-testid="newsletter-section">
          <div className="newsletter-content">
            <Mail size={32} className="newsletter-icon" />
            <h3>Stay Updated</h3>
            <p>Subscribe to receive insights on India-Singapore business growth directly in your inbox.</p>
            <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
              <input
                type="email"
                placeholder="Enter your email"
                value={newsletterEmail}
                onChange={(e) => setNewsletterEmail(e.target.value)}
                required
                data-testid="newsletter-email"
              />
              <button 
                type="submit" 
                className="btn-primary"
                disabled={subscribing}
                data-testid="newsletter-submit"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </form>
            <p className="newsletter-privacy">We respect your privacy. Unsubscribe anytime.</p>
          </div>
        </div>

        <div className="blog-post-footer">
          <div className="blog-post-cta">
            <h3>Interested in learning more?</h3>
            <p>Schedule a consultation to discuss how we can help your business grow.</p>
            <button className="btn-primary" onClick={() => navigate('/#contact')}>
              Contact Us
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
